/* compare.js — Comparaison de deux fichiers : appariement des pistes par
 * faisceau d'indices, critères et avantage, profil « mon fichier idéal »,
 * synthèse. Textes centralisés ici. Expose self.MediaCompare. */
'use strict';

import F from './format.js';
import R from './rules.js';


var LABELS = {
  pairs: 'Pistes appariées',
  onlyA: 'A seulement', onlyB: 'B seulement',
  criteria: 'Critères', sameCriteria: 'Critères identiques',
  advantage: { A: 'A', B: 'B', equal: 'équivalent', unknown: 'non évaluable' },
  indifferent: 'indifférent pour vous',
  summary: 'Synthèse',
  concluded: 'Ce que les métadonnées permettent de dire',
  needsImage: 'Ce qui exige de regarder l’image',
  betterOn: '{side} est supérieur sur : {list}.',
  equalOn: 'Équivalents sur : {list}.',
  oneSided: '{track} : présente dans {side} seulement.',
  classification: 'Classification : indéterminée — l’image n’a pas été analysée.',
  noRecommendation: 'Pas de recommandation « garder A » ou « garder B » : les valeurs déclarées ne suffisent pas à trancher, et le débit seul ne dit pas quelle image est la meilleure.',
  combine: 'Une piste présente d’un seul côté peut être récupérée dans l’autre version plutôt que de choisir.',
  duration: 'Durées : A {a}, B {b} (écart {diff}).',
  ambiguous: 'Appariement ambigu : choisissez la correspondance.',
  ideal: 'Mon fichier idéal',
  idealVerdict: 'Conformité à mon fichier idéal',
  idealHint: 'Décrivez ce que vous attendez d’un fichier : il est évalué pour les deux versions, et les critères indifférents sortent de la synthèse. Enregistré dans ce navigateur.',
  audioLanguages: 'Langues audio voulues',
  subtitleLanguages: 'Sous-titres voulus',
  prefs: { lossless: 'Audio sans perte', surround: 'Audio multicanal (5.1 et plus)', hdr: 'HDR', size: 'Fichier plus léger' },
  prefValues: { prefer: 'souhaité', neutral: 'sans préférence', indifferent: 'indifférent' },
  apply: 'Appliquer',
  type: 'Type', criterion: 'Critère', advantageHead: 'Avantage', reasonsHead: 'Indices',
  recover: 'Récupérer une piste',
  into: 'dans {side}',
  look: 'Regarder l’image',
  lookHint: 'Cinq images sont extraites de chaque version au même endroit du film, en pleine définition, et posées côte à côte. Cliquez dans une image pour voir les deux au point près, au même endroit : c’est là que se jugent la netteté, le grain et les défauts de compression — et nulle part dans les métadonnées.',
  lookRun: 'Extraire les images',
  lookAgain: 'Extraire à nouveau',
  lookBusy: 'Extraction en cours…',
  lookFit: 'Vue d’ensemble',
  lookZoomHint: 'Cliquez dans une image pour l’examiner au point près.',
  lookZoomOn: 'Vue au point près, au même endroit des deux côtés. Cliquez ailleurs pour vous déplacer.',
  lookDifferent: 'Ces deux images ne montrent pas le même plan : les versions n’ont pas le même début, et les comparer ici ne veut rien dire.',
  lookProportional: 'Les deux versions n’ont pas la même durée : les images sont prises aux mêmes proportions du film, et non aux mêmes secondes.',
  lookScaled: 'réduite à 1920 points de large',
  lookNoVideo: 'Pas de piste vidéo à extraire.',
  lookPosition: 'Position {n}'
};

// Ce qui exige de regarder l'image.
var NEEDS_IMAGE = {
  master: 'Même master ou non : cadrage, colorimétrie et texture se comparent sur l’image.',
  surface: 'Surface d’image utile : {a} contre {b} déclarés ; des bandes noires encodées ne se voient qu’à l’image.',
  quality: 'Qualité réelle de l’encodage : un débit plus élevé ne garantit pas une meilleure image.',
  edit: 'Correspondance du montage : l’écart de durée peut venir d’un générique, d’un carton ou d’un montage différent.'
};

var REASONS = {
  language: 'même langue ({v})',
  languageDiffers: 'langues différentes ({a} / {b})',
  title: 'même titre',
  codec: 'même codec ({v})',
  channels: 'même nombre de canaux ({v})',
  forced: 'même drapeau « forcé »',
  forcedDiffers: 'forcé : {a} / {b}',
  duration: 'durées proches'
};

var KIND_ORDER = ['video', 'audio', 'text'];
var KIND_LABEL = { video: 'Vidéo', audio: 'Audio', text: 'Sous-titres' };
var LOSSLESS = ['PCM', 'FLAC', 'ALAC', 'MLP FBA'];

function fill(tpl, p) { return String(tpl).replace(/\{(\w+)\}/g, function (m, k) { return p && p[k] != null ? String(p[k]) : m; }); }
function fps(v) { return v == null ? null : String(v).replace('.', ',') + ' i/s'; }
function yn(v) { return v === true ? 'oui' : v === false ? 'non' : '?'; }
function tracks(m, kind) { return m.tracks.filter(function (t) { return t.kind === kind; }); }
function norm(s) { return s ? String(s).toLowerCase().replace(/[^a-z0-9]+/g, '') : ''; }

/* ---------- Appariement ---------- */
/* Indices d'appariement. La langue d'une vidéo, souvent mal déclarée, n'en est
 * pas un ; deux vidéos partent d'un score positif. */
function score(a, b) {
  var s = a.kind === 'video' ? 1 : 0, reasons = [];
  var la = a.language && a.language.code, lb = b.language && b.language.code;
  if (a.kind !== 'video' && la && lb) {
    if (la === lb) { s += 4; reasons.push(fill(REASONS.language, { v: la })); }
    else { s -= 4; reasons.push(fill(REASONS.languageDiffers, { a: la, b: lb })); }
  }
  if (norm(a.title) && norm(a.title) === norm(b.title)) { s += 2; reasons.push(REASONS.title); }
  if (a.formatRaw && a.formatRaw === b.formatRaw) { s += 1; reasons.push(fill(REASONS.codec, { v: a.formatRaw })); }
  if (a.kind === 'audio' && a.channels != null && a.channels === b.channels) { s += 1; reasons.push(fill(REASONS.channels, { v: a.channels })); }
  if (a.kind === 'text' && a.forced != null && b.forced != null) {
    if (a.forced === b.forced) { s += 1; reasons.push(REASONS.forced); }
    else { s -= 1; reasons.push(fill(REASONS.forcedDiffers, { a: yn(a.forced), b: yn(b.forced) })); }
  }
  if (a.duration && b.duration && Math.abs(a.duration - b.duration) / Math.max(a.duration, b.duration) < 0.02) { s += 1; reasons.push(REASONS.duration); }
  return { score: s, reasons: reasons };
}

/* Affectations injectives de score maximal ; un score nul ou négatif n'est pas
 * retenu. Plus d'une affectation : ambiguïté. */
function bestAssignments(la, lb) {
  var best = -Infinity, found = [];
  var M = la.map(function (a) { return lb.map(function (b) { return score(a, b); }); });
  function walk(i, used, acc, total) {
    if (i === la.length) {
      var key = acc.join(',');
      if (total > best) { best = total; found = [acc.slice()]; }
      else if (total === best && found.every(function (f) { return f.join(',') !== key; })) found.push(acc.slice());
      return;
    }
    acc.push(-1); walk(i + 1, used, acc, total); acc.pop();
    for (var j = 0; j < lb.length; j++) {
      if (used[j] || M[i][j].score <= 0) continue;
      used[j] = true; acc.push(j); walk(i + 1, used, acc, total + M[i][j].score); acc.pop(); used[j] = false;
    }
  }
  if (la.length > 7 || lb.length > 7) { // au-delà, affectation gloutonne
    var used = {}, acc = [];
    la.forEach(function (a, i) {
      var bj = -1;
      lb.forEach(function (b, j) { if (!used[j] && M[i][j].score > 0 && (bj < 0 || M[i][j].score > M[i][bj].score)) bj = j; });
      if (bj >= 0) used[bj] = true;
      acc.push(bj);
    });
    return { assignments: [acc], matrix: M };
  }
  walk(0, {}, [], 0);
  return { assignments: found, matrix: M };
}

/* Paires { kind, a, b, reasons, ambiguous } (a, b : ordinal ou null).
 * manual : { kind: { aOrdinal: bOrdinal | null } } remplace l'appariement d'un type. */
function pairTracks(ma, mb, manual) {
  var out = [];
  KIND_ORDER.forEach(function (kind) {
    var la = tracks(ma, kind), lb = tracks(mb, kind);
    if (!la.length && !lb.length) return;
    var res = bestAssignments(la, lb);
    var assign = res.assignments[0] || la.map(function () { return -1; });
    var ambiguous = res.assignments.length > 1;
    if (manual && manual[kind]) {
      assign = la.map(function (a) {
        var bo = manual[kind][a.ordinal];
        if (bo === undefined) return -1;
        for (var j = 0; j < lb.length; j++) if (lb[j].ordinal === bo) return j;
        return -1;
      });
      ambiguous = false;
    }
    var usedB = {};
    la.forEach(function (a, i) {
      var j = assign[i];
      if (j >= 0) {
        usedB[j] = true;
        out.push({ kind: kind, a: a.ordinal, b: lb[j].ordinal, reasons: res.matrix[i][j].reasons, ambiguous: ambiguous });
      } else out.push({ kind: kind, a: a.ordinal, b: null, reasons: [], ambiguous: ambiguous });
    });
    lb.forEach(function (b, j) { if (!usedB[j]) out.push({ kind: kind, a: null, b: b.ordinal, reasons: [], ambiguous: ambiguous }); });
  });
  return out;
}

/* ---------- Mon fichier idéal ----------
 * settings : { audioLanguages: [iso1], subtitleLanguages: [iso1],
 *   lossless, surround, hdr, size : 'prefer' | 'neutral' | 'indifferent' }.
 * prefer ajoute une règle ; indifferent sort le critère de la synthèse. */
var IDEAL_DEFAULT = { audioLanguages: [], subtitleLanguages: [], lossless: 'neutral', surround: 'neutral', hdr: 'neutral', size: 'neutral' };

function idealProfile(st) {
  st = Object.assign({}, IDEAL_DEFAULT, st);
  var rules = [];
  st.audioLanguages.forEach(function (l) {
    rules.push({ id: 'audio-' + l, track: 'audio', quantifier: 'any', field: 'language.code', op: 'eq', value: l, severity: 'error', message: 'Audio en « ' + l + ' » voulu.' });
  });
  st.subtitleLanguages.forEach(function (l) {
    rules.push({ id: 'sub-' + l, track: 'text', quantifier: 'any', field: 'language.code', op: 'eq', value: l, severity: 'error', message: 'Sous-titres en « ' + l + ' » voulus.' });
  });
  if (st.lossless === 'prefer') rules.push({ id: 'lossless', track: 'audio', quantifier: 'any', field: 'formatRaw', op: 'inList', value: LOSSLESS, severity: 'warning', message: 'Audio sans perte souhaité.' });
  if (st.surround === 'prefer') rules.push({ id: 'surround', track: 'audio', quantifier: 'any', field: 'channels', op: 'gte', value: 6, severity: 'warning', message: 'Audio multicanal (5.1 ou plus) souhaité.' });
  if (st.hdr === 'prefer') rules.push({ id: 'hdr', track: 'video', quantifier: 'any', field: 'hdrFormat', op: 'exists', severity: 'warning', message: 'HDR souhaité.' });
  return { id: 'mon-fichier-ideal', name: LABELS.ideal, version: '1', statut: 'personnel', appliesTo: {}, rules: rules };
}

/* ---------- Critères ---------- */
var VERDICT_RANK = { 'conforme': 3, 'conforme-avert': 2, 'indeterminate': 1, 'non-conforme': 0 };

function higher(a, b) { return a == null || b == null ? 'unknown' : a > b ? 'A' : b > a ? 'B' : 'equal'; }
function lower(a, b) { var h = higher(a, b); return h === 'A' ? 'B' : h === 'B' ? 'A' : h; }

function row(section, label, va, vb, textA, textB, advantage, opts) {
  var r = { section: section, label: label,
    a: { value: va, text: textA == null ? '—' : String(textA), origin: 'declared' },
    b: { value: vb, text: textB == null ? '—' : String(textB), origin: 'declared' },
    advantage: advantage };
  r.same = r.a.text === r.b.text;
  if (r.same && advantage !== null) r.advantage = 'equal';
  if (opts && opts.indifferent) r.indifferent = true;
  if (opts && opts.note) r.note = opts.note;
  return r;
}

function byOrdinal(m, kind, n) { return tracks(m, kind).filter(function (t) { return t.ordinal === n; })[0]; }
function lang(t) { return t && t.language && (t.language.name || t.language.raw) || '—'; }

/* Lignes du tableau : { section, label, a, b, advantage (A | B | equal | unknown,
 * ou null pour une valeur seulement informative), same, indifferent?, note? }. */
function criteria(ma, mb, pairs, st, reports) {
  st = Object.assign({}, IDEAL_DEFAULT, st);
  var fmt = F.fmt, rows = [];
  var ca = ma.container, cb = mb.container;
  rows.push(row('file', 'Taille', ca.fileSize, cb.fileSize, fmt.bytes(ca.fileSize), fmt.bytes(cb.fileSize),
    lower(ca.fileSize, cb.fileSize), { indifferent: st.size === 'indifferent' }));
  rows.push(row('file', 'Durée', ca.duration, cb.duration, fmt.duration(ca.duration), fmt.duration(cb.duration), null));
  rows.push(row('file', 'Conteneur', ca.formatRaw, cb.formatRaw, ca.format, cb.format, null));
  rows.push(row('file', 'Chapitres', ma.chapters.length, mb.chapters.length, ma.chapters.length, mb.chapters.length,
    higher(ma.chapters.length, mb.chapters.length)));

  pairs.forEach(function (p) {
    var ta = p.a != null ? byOrdinal(ma, p.kind, p.a) : null, tb = p.b != null ? byOrdinal(mb, p.kind, p.b) : null;
    var name = KIND_LABEL[p.kind] + (ta ? ' ' + lang(ta) : ' ' + lang(tb));
    if (!ta || !tb) {
      rows.push(row(p.kind, name, !!ta, !!tb, ta ? 'présente' : 'absente', tb ? 'présente' : 'absente', ta ? 'A' : 'B'));
      rows[rows.length - 1].oneSided = true;
      return;
    }
    var sameCodec = ta.formatRaw === tb.formatRaw;
    if (p.kind === 'video') {
      rows.push(row('video', 'Codec vidéo', ta.formatRaw, tb.formatRaw, ta.format, tb.format, sameCodec ? 'equal' : 'unknown'));
      rows.push(row('video', 'Définition déclarée', ta.width + '×' + ta.height, tb.width + '×' + tb.height,
        ta.width + ' × ' + ta.height, tb.width + ' × ' + tb.height, 'unknown', { note: 'surface' }));
      rows.push(row('video', 'Débit vidéo', ta.bitRate, tb.bitRate, fmt.bitrate(ta.bitRate), fmt.bitrate(tb.bitRate),
        sameCodec ? higher(ta.bitRate, tb.bitRate) : 'unknown', { note: 'quality' }));
      rows.push(row('video', 'Profondeur', ta.bitDepth, tb.bitDepth, ta.bitDepth && ta.bitDepth + ' bits', tb.bitDepth && tb.bitDepth + ' bits', higher(ta.bitDepth, tb.bitDepth)));
      rows.push(row('video', 'HDR', !!ta.hdrFormat, !!tb.hdrFormat, ta.hdrFormat || 'non', tb.hdrFormat || 'non',
        ta.hdrFormat && !tb.hdrFormat ? 'A' : tb.hdrFormat && !ta.hdrFormat ? 'B' : 'equal', { indifferent: st.hdr === 'indifferent' }));
      rows.push(row('video', 'Cadence', ta.frameRate, tb.frameRate, fps(ta.frameRate), fps(tb.frameRate), null));
    } else if (p.kind === 'audio') {
      var lossA = LOSSLESS.indexOf(ta.formatRaw) >= 0, lossB = LOSSLESS.indexOf(tb.formatRaw) >= 0;
      rows.push(row('audio', name + ' — format', ta.formatRaw, tb.formatRaw, ta.format, tb.format,
        lossA !== lossB ? (lossA ? 'A' : 'B') : sameCodec ? 'equal' : 'unknown', { indifferent: lossA !== lossB && st.lossless === 'indifferent' }));
      rows.push(row('audio', name + ' — canaux', ta.channels, tb.channels, ta.channelLayout && ta.channelLayout.friendly, tb.channelLayout && tb.channelLayout.friendly,
        higher(ta.channels, tb.channels), { indifferent: st.surround === 'indifferent' && (ta.channels > 2 || tb.channels > 2) }));
      rows.push(row('audio', name + ' — débit', ta.bitRate, tb.bitRate, fmt.bitrate(ta.bitRate), fmt.bitrate(tb.bitRate),
        sameCodec ? higher(ta.bitRate, tb.bitRate) : 'unknown'));
    } else {
      rows.push(row('text', name + ' — forcés', ta.forced, tb.forced, yn(ta.forced), yn(tb.forced), null));
    }
  });

  if (reports) {
    var va = reports.a.verdict, vb = reports.b.verdict;
    rows.push(row('ideal', LABELS.idealVerdict, va.code, vb.code, va.label, vb.label, higher(VERDICT_RANK[va.code], VERDICT_RANK[vb.code])));
  }
  return rows;
}

/* ---------- Synthèse v0 ---------- */
function summary(ma, mb, rows, pairs) {
  var concluded = [], needsImage = [NEEDS_IMAGE.master];
  var da = ma.container.duration, db = mb.container.duration;
  if (da && db) {
    concluded.push(fill(LABELS.duration, { a: F.fmt.duration(da), b: F.fmt.duration(db),
      diff: (Math.round(Math.abs(da - db) * 10) / 10).toLocaleString('fr-FR') + ' s' }));
    if (Math.abs(da - db) > 1) needsImage.push(NEEDS_IMAGE.edit);
  }
  var judged = rows.filter(function (r) { return r.advantage !== null && !r.indifferent && !r.oneSided; });
  ['A', 'B'].forEach(function (side) {
    var list = judged.filter(function (r) { return r.advantage === side; }).map(function (r) { return r.label; });
    if (list.length) concluded.push(fill(LABELS.betterOn, { side: side, list: list.join(', ') }));
  });
  var eq = judged.filter(function (r) { return r.advantage === 'equal'; }).map(function (r) { return r.label; });
  if (eq.length) concluded.push(fill(LABELS.equalOn, { list: eq.join(', ') }));
  var oneSided = rows.filter(function (r) { return r.oneSided; });
  oneSided.forEach(function (r) { concluded.push(fill(LABELS.oneSided, { track: r.label, side: r.advantage })); });
  var def = rows.filter(function (r) { return r.label === 'Définition déclarée'; })[0];
  if (def && !def.same) needsImage.push(fill(NEEDS_IMAGE.surface, { a: def.a.text, b: def.b.text }));
  var br = rows.filter(function (r) { return r.note === 'quality'; })[0];
  if (br && !br.same) needsImage.push(NEEDS_IMAGE.quality);
  return {
    classification: { value: 'indeterminate', text: LABELS.classification },
    concluded: concluded,
    needsImage: needsImage,
    recommendation: { value: null, text: LABELS.noRecommendation + (oneSided.length ? ' ' + LABELS.combine : '') }
  };
}

/* ---------- Récupérer une piste de l'autre version ----------
 * Une action par piste présente d'un seul côté, visant celle qui ne l'a pas. */
function addTrackActions(ma, mb, pairs, names) {
  var out = [];
  pairs.forEach(function (p) {
    if (p.kind === 'video' || (p.a != null && p.b != null)) return;
    var fromA = p.a != null;
    var src = byOrdinal(fromA ? ma : mb, p.kind, fromA ? p.a : p.b);
    out.push({
      into: fromA ? 'B' : 'A',
      action: {
        type: 'add-track', target: { kind: 'container' }, nature: 'remux', warnings: [], resolves: [],
        params: { from: fromA ? names.a : names.b, kind: p.kind, tid: src.streamOrder, label: KIND_LABEL[p.kind] + ' ' + lang(src),
          sync: Math.abs((ma.container.duration || 0) - (mb.container.duration || 0)) > 0.5 }
      }
    });
  });
  out.forEach(function (x) { if (x.action.params.sync) x.action.warnings.push('sync'); });
  return out;
}

function compare(ma, mb, opts) {
  opts = opts || {};
  var pairs = pairTracks(ma, mb, opts.manual);
  var reports = null;
  if (opts.ideal && R) {
    var prof = idealProfile(opts.ideal);
    if (prof.rules.length) reports = { a: R.evaluate(prof, ma), b: R.evaluate(prof, mb) };
  }
  var rows = criteria(ma, mb, pairs, opts.ideal, reports);
  return { pairs: pairs, rows: rows, reports: reports, summary: summary(ma, mb, rows, pairs),
    recover: opts.names ? addTrackActions(ma, mb, pairs, opts.names) : [] };
}

var api = {
  LABELS: LABELS, NEEDS_IMAGE: NEEDS_IMAGE, IDEAL_DEFAULT: IDEAL_DEFAULT, KIND_LABEL: KIND_LABEL,
  compare: compare, pairTracks: pairTracks, criteria: criteria, summary: summary,
  idealProfile: idealProfile, addTrackActions: addTrackActions, _score: score
};
globalThis.MediaCompare = api;
export default api;
